import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { copilotApi } from '../../services/copilotApi';
import { useSubscription } from '../../contexts/SubscriptionContext';

interface BillingInvoice {
  id: string | null;
  number: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string | null;
  created: number; // epoch seconds
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

interface BillingPlan {
  product_name: string;
  unit_amount: number | null;
  currency: string | null;
  interval: string | null;
  current_period_end: number | null; // epoch seconds
  cancel_at_period_end: boolean;
  status: string;
}

interface BillingResponse {
  has_billing: boolean;
  invoices: BillingInvoice[];
  plan: BillingPlan | null;
}

/** Format a minor-unit amount (e.g. 4900) + currency code into a localized price. */
function formatAmount(minor: number | null | undefined, currency: string | null | undefined): string {
  if (minor == null || !currency) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function formatDate(epochSeconds: number | null | undefined): string {
  if (!epochSeconds) return '—';
  return new Date(epochSeconds * 1000).toLocaleDateString();
}

const invoiceStatusColor = (
  status: string | null
): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'open':
      return 'warning';
    case 'uncollectible':
    case 'void':
      return 'error';
    default:
      return 'default';
  }
};

const BillingSettings: React.FC = () => {
  const { subscription, openBillingPortal } = useSubscription();
  const [data, setData] = useState<BillingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await copilotApi.get<BillingResponse>('/subscription/billing');
      setData(response);
    } catch (err) {
      console.error('Failed to load billing info:', err);
      setError('Could not load your billing information. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handlePortal = async () => {
    try {
      setPortalBusy(true);
      await openBillingPortal();
      // Browser redirects to Stripe on success.
    } catch {
      setError('Could not open the billing portal. Please try again.');
      setPortalBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => void load()} variant="outlined">
          Retry
        </Button>
      </Box>
    );
  }

  // Access-code (or any non-Stripe) subscription: no invoices/portal.
  if (!data?.has_billing) {
    return (
      <Stack spacing={2}>
        <Typography variant="h6">Current plan</Typography>
        <Box>
          <Typography variant="body1" fontWeight={600}>
            {subscription?.subscription_title || 'Access Code'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subscription?.valid_until
              ? `Access until ${new Date(subscription.valid_until).toLocaleDateString()}`
              : 'Unlimited access'}
          </Typography>
        </Box>
        <Alert severity="info">
          Your access is provided through an access code, so there are no invoices or payment
          method to manage here.
        </Alert>
      </Stack>
    );
  }

  const plan = data.plan;
  const pastDue = plan?.status === 'past_due';

  return (
    <Stack spacing={3}>
      {pastDue && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handlePortal} disabled={portalBusy}>
              Update card
            </Button>
          }
        >
          Your last payment failed. Please update your payment method to keep your subscription
          active.
        </Alert>
      )}

      {/* Current plan */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Current plan
        </Typography>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
          <Typography variant="body1" fontWeight={600}>
            {plan?.product_name || subscription?.subscription_title || 'Subscription'}
          </Typography>
          {plan?.unit_amount != null && (
            <Typography variant="body1" color="text.secondary">
              {formatAmount(plan.unit_amount, plan.currency)}
              {plan.interval ? ` / ${plan.interval}` : ''}
            </Typography>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {plan?.cancel_at_period_end
            ? `Cancels on ${formatDate(plan.current_period_end)}`
            : plan?.current_period_end
              ? `Renews on ${formatDate(plan.current_period_end)}`
              : ''}
        </Typography>
        <Button
          variant="contained"
          startIcon={portalBusy ? <CircularProgress size={18} color="inherit" /> : <CreditCardIcon />}
          onClick={handlePortal}
          disabled={portalBusy}
          sx={{ mt: 2 }}
        >
          {portalBusy ? 'Opening…' : 'Manage subscription & payment method'}
        </Button>
      </Box>

      <Divider />

      {/* Invoices */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Invoices
        </Typography>
        {data.invoices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No invoices yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.invoices.map((inv) => (
                <TableRow key={inv.id || inv.number || inv.created}>
                  <TableCell>{formatDate(inv.created)}</TableCell>
                  <TableCell>{inv.number || '—'}</TableCell>
                  <TableCell align="right">
                    {formatAmount(inv.amount_paid || inv.amount_due, inv.currency)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={inv.status || 'unknown'}
                      color={invoiceStatusColor(inv.status)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {inv.hosted_invoice_url ? (
                      <Link
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        View <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Stack>
  );
};

export default BillingSettings;
